'use client';

import Image from '@/components/Image';
import { useStore } from '@/providers/app-store-provider';
import { EPlatform } from '@/utils/enums/platform-enum';
import { formatPriceNumberWithSymbol, priceInfo } from '@/utils/price';
import { useAppNavigation } from '@/hooks/useAppNavigation';
import { ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import { Navigation } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
// @ts-ignore: Icon library missing type definitions
import { LeftArrowOutlined, RightArrowOutlined } from '@zeelool/icon';
import './activity-product-list-navigation.css';

// 商品数据类型定义
export interface HotProduct {
  id?: number | string;
  name?: string;
  short_name?: string;
  shop_sku?: string;
  business_sku?: string;
  sku?: string;
  image?: {
    product?: string[];
    celebrity?: string[];
  };
  price_info?: Array<{
    currency?: string;
    price?: string;
    sell_price?: string;
  }>;
  price_info_v1?: Array<{
    currency?: string;
    price?: string;
    sell_price?: string;
  }>;
  price_info_v2?: Array<{
    currency?: string;
    price?: string;
    sell_price?: string;
  }>;
  price_info_v3?: Array<{
    currency?: string;
    price?: string;
    sell_price?: string;
  }>;
  discount_percent_v1?: number | string;
  discount_percent_v2?: number | string;
  discount_percent_v3?: number | string;
  [key: string]: any;
}

// 商品项组件Props类型
interface ProductItemProps {
  product: HotProduct;
  index: number;
  isWAP: boolean;
  currency: any;
  getPriceInfo: (product: HotProduct) => any;
  getProductImage: (product: HotProduct) => string;
  getHoverImage: (product: HotProduct) => string;
  onProductClick: (product: HotProduct, index: number) => void;
  onProductExp: (product: HotProduct, index: number) => void;
}

// 商品项组件 - 包含曝光检测
const ProductItem = ({
  product,
  index,
  isWAP,
  currency,
  getPriceInfo,
  getProductImage,
  getHoverImage,
  onProductClick,
  onProductExp,
}: ProductItemProps) => {
  const [productRef, inView] = useInView({
    threshold: 0.8,
    triggerOnce: true,
  });

  // 曝光检测
  useEffect(() => {
    if (inView) {
      onProductExp(product, index);
    }
  }, [inView, product, index, onProductExp]);

  const productPriceInfo = getPriceInfo(product);
  const productImage = getProductImage(product);
  const hoverImage = getHoverImage(product);

  return (
    <div
      ref={productRef}
      className={`product-card cursor-pointer overflow-hidden bg-[#31384a] ${
        !isWAP && hoverImage ? 'group' : ''
      }`}
      style={{
        borderRadius: isWAP ? '7px' : '21px',
      }}
      onClick={() => onProductClick(product, index)}
    >
      {/* 商品图片 */}
      <div
        className="relative flex items-center justify-center aspect-square bg-white overflow-hidden"
        style={{ borderRadius: isWAP ? '7px' : '21px' }}
      >
        <Image
          src={productImage}
          alt={product.name || product.short_name || ''}
          width={300}
          height={300}
          className="block w-[80%] mx-auto"
        />

        {/* PC端悬浮图片 */}
        {!isWAP && hoverImage && (
          <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300 overflow-hidden">
            <Image
              src={hoverImage}
              alt={`${product.name || product.short_name || ''} hover`}
              width={300}
              height={300}
              className="w-[90%] h-auto object-contain"
            />
          </div>
        )}
      </div>

      {/* 商品信息 */}
      <div
        className={`
        box-border text-white
        ${isWAP ? 'p-[10px_6px_8px]' : 'h-[86px] p-[12px_15px_12px_15px] xl:p-[14px_20px_14px_20px] 2xl:p-[16px_30px_16px_30px]'}
      `}
      >
        {/* 商品名称 */}
        <p
          className={`
          text-white ${isWAP ? 'mb-0 text-[11px]' : 'mb-[8px] text-[12px] xl:text-[14px] 2xl:text-[16px] font-bb'}
        `}
        >
          {product.short_name || product.name || ''}
        </p>

        {/* 价格信息 */}
        <div className="price">
          <span
            className={`
            text-white font-bb
            ${isWAP ? 'text-[12px] leading-[13px] pr-[5px]' : 'text-[12px] xl:text-[14px] 2xl:text-[16px]'}
          `}
          >
            {formatPriceNumberWithSymbol(productPriceInfo?.sell_price || '0', currency)}
          </span>

          {productPriceInfo?.sell_price !== productPriceInfo?.price && productPriceInfo?.price && (
            <span
              className={`
              text-[#c4c4c4] line-through
              ${isWAP ? 'text-[11px] leading-[13px]' : 'text-[12px] xl:text-[14px] 2xl:text-[16px] pl-[10px]'}
            `}
            >
              {formatPriceNumberWithSymbol(productPriceInfo.price, currency)}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

// 组件Props类型定义
export interface HotProductSwiperProps {
  // 基本配置
  className?: string;

  // 标题配置
  title?: string;

  // 商品数据 - 由服务器端传入
  products: HotProduct[];

  // 布局配置
  deviceType?: EPlatform;

  // 样式主题
  theme?: 'blueLight' | 'driving';

  // 事件回调
  onProductClick?: (product: HotProduct, index: number) => void;
  onProductExp?: (product: HotProduct, index: number) => void;

  // 自定义渲染
  customEmptyState?: ReactNode;

  // 时间戳用于生成唯一ID
  ts?: number;
}

export default function HotProductSwiper({
  className = '',
  title,
  products = [],
  deviceType,
  theme = 'blueLight',
  onProductClick,
  onProductExp,
  customEmptyState,
  ts = Date.now(),
}: HotProductSwiperProps) {
  // 轮播相关状态
  const [isBeginning, setIsBeginning] = useState(true);
  const [isEnd, setIsEnd] = useState(false);

  const currency = useStore((state) => state.currency);
  const userInfo = useStore((state) => state.userInfo);
  const { navigate } = useAppNavigation();

  const swiperRef = useRef<any>(null);

  // 判断设备类型
  const isWAP =
    deviceType !== EPlatform.WEB || (typeof window !== 'undefined' && window.innerWidth < 1024);

  // 生成唯一ID用于导航按钮
  const uniqueId = `hot-product-swiper-${theme}-${ts}`;

  // 替换图标函数
  const replaceIcons = (content: string) => {
    try {
      if (typeof content !== 'string') return content;
      return content
        .replace(/<i class="iconfont icon-shangbiao"><\/i>/g, '®')
        .replace(/<i class="iconfont icon-line-trademarkshangbiao"><\/i>/g, '™');
    } catch {
      return content;
    }
  };

  // 使用全局 priceInfo 方法获取商品价格信息
  const getPriceInfo = useCallback(
    (product: HotProduct) => {
      return priceInfo(userInfo, product, currency);
    },
    [userInfo, currency],
  );

  // 获取商品主图
  const getProductImage = useCallback((product: HotProduct) => {
    const productImages = product?.image?.product || [];
    return productImages.length > 0 ? productImages[0] : '';
  }, []);

  // 获取商品悬浮图（红人图）
  const getHoverImage = useCallback((product: HotProduct) => {
    const celebrityImages = product?.image?.celebrity || [];
    return celebrityImages.length > 0 ? celebrityImages[0] : '';
  }, []);

  // 处理商品点击
  const handleProductClick = useCallback(
    (product: HotProduct, index: number) => {
      // 执行外部回调（埋点）
      onProductClick?.(product, index);

      // 跳转到商品详情页，使用navigate方法支持app内嵌环境
      const productUrl = `/goods-detail/${product.shop_sku}`;
      navigate(productUrl);
    },
    [onProductClick, navigate],
  );

  // 处理商品曝光
  const handleProductExp = useCallback(
    (product: HotProduct, index: number) => {
      // 执行外部回调（埋点）
      onProductExp?.(product, index);
    },
    [onProductExp],
  );

  // PC端Swiper配置 - 4.5个商品
  const pcSwiperOptions = {
    modules: [Navigation],
    observer: true,
    observeParents: true,
    observeSlideChildren: true,
    slidesPerView: 4.1,
    spaceBetween: 16,
    slidesPerGroup: 1,
    speed: 300,
    navigation:
      theme === 'driving'
        ? false
        : {
            nextEl: `.${uniqueId}-next`,
            prevEl: `.${uniqueId}-prev`,
          },
    onSlideChange: (swiper: any) => {
      setIsBeginning(swiper.isBeginning);
      setIsEnd(swiper.isEnd);
    },
    onInit: (swiper: any) => {
      setIsBeginning(swiper.isBeginning);
      setIsEnd(swiper.isEnd);
    },
  };

  // 移动端Swiper配置 - 2.5个商品
  const mobileSwiperOptions = {
    modules: [Navigation],
    observer: true,
    observeParents: true,
    observeSlideChildren: true,
    slidesPerView: 2.2,
    spaceBetween: 8,
    slidesPerGroup: 1,
    speed: 300,
    onSlideChange: (swiper: any) => {
      setIsBeginning(swiper.isBeginning);
      setIsEnd(swiper.isEnd);
    },
    onInit: (swiper: any) => {
      setIsBeginning(swiper.isBeginning);
      setIsEnd(swiper.isEnd);
    },
  };

  const swiperOptions = isWAP ? mobileSwiperOptions : pcSwiperOptions;

  // 获取主题样式类
  const getThemeClasses = () => {
    const baseClasses = 'hot-product-swiper relative';

    if (theme === 'driving') {
      return `${baseClasses} driving-theme`;
    }

    return `${baseClasses} blue-light-theme border-b border-[#3e4758] pb-[20px] xl:pb-[45px] 2xl:pb-[57px]`;
  };

  // 获取标题样式类
  const getTitleClasses = () => {
    if (isWAP) {
      return 'text-[20px] font-bb text-white leading-[20px] text-left';
    }

    if (theme === 'driving') {
      return 'text-[40px] font-bb text-white leading-[40px] text-center';
    }

    return 'text-[40px] font-bb text-white leading-[40px] text-left';
  };

  // 获取标题容器padding样式
  const getTitleContainerPadding = () => {
    if (isWAP) {
      return 'px-[24px] pl-[16px]';
    }

    return 'px-[120px]';
  };

  // 获取标题padding样式
  const getTitlePaddingClass = (): string => {
    if (isWAP) {
      return 'pt-[32px] pb-[16px]';
    }
    if (theme === 'driving') {
      return 'pt-[40px] pb-[32px]';
    }
    return 'pt-[60px] pb-[32px]';
  };

  // 显示空状态
  if (!products || products.length === 0) {
    return customEmptyState || null;
  }

  return (
    <div
      className={`${getThemeClasses()} ${className} product-swiper-container hot-product-swiper-always-show-nav`}
    >
      {/* 标题 */}
      {title && (
        <div className={`${getTitlePaddingClass()} ${getTitleContainerPadding()}`}>
          <h4
            className={`${getTitleClasses()} hot-list-title`}
            dangerouslySetInnerHTML={{ __html: replaceIcons(title) }}
          />
        </div>
      )}

      {/* 商品轮播 */}
      <div
        className={`relative ${theme === 'driving' ? 'pb-[20px] xl:pb-[80px] 2xl:pb-[120px]' : ''}`}
      >
        <Swiper
          ref={swiperRef}
          {...swiperOptions}
          className={`hot-product-swiper-container blue-light-swiper-box opacity0`}
          style={{ padding: isWAP ? '0 16px' : '0 120px 0' }}
        >
          {products.map((product, index) => (
            <SwiperSlide
              key={`${product.shop_sku || product.id}-${index}`}
              className={`swiper-item relative hot-list-product-item ${index < 5 ? 'hasAnimate' : ''}`}
            >
              <ProductItem
                product={product}
                index={index}
                isWAP={isWAP}
                currency={currency}
                getPriceInfo={getPriceInfo}
                getProductImage={getProductImage}
                getHoverImage={getHoverImage}
                onProductClick={handleProductClick}
                onProductExp={handleProductExp}
              />
            </SwiperSlide>
          ))}
        </Swiper>
      </div>

      {/* PC端蓝光主题导航按钮 - 与标题在同一行 */}
      {!isWAP && theme === 'blueLight' && products.length > 4 && (
        <>
          {/* 向右按钮 */}
          <div
            className={`
              ${uniqueId}-next product-swiper-navigation absolute z-20 flex items-center justify-center
              w-[2.08vw] h-[2.08vw]
              top-[3.23vw] right-[6.25vw]
              rounded-full border text-white transition-transform duration-200
              ${
                isEnd
                  ? 'cursor-not-allowed !border-[#8d8d92] hover:scale-100'
                  : 'cursor-pointer border-white hover:scale-110'
              }
            `}
            style={{
              backgroundColor: 'transparent',
            }}
            onClick={() => !isEnd && swiperRef.current?.swiper?.slideNext()}
          >
            <RightArrowOutlined
              className={`text-[1.04vw] relative left-[2px] ${isEnd ? 'text-[#8d8d92]' : 'text-white'}`}
            />
          </div>

          {/* 向左按钮 */}
          <div
            className={`
              ${uniqueId}-prev product-swiper-navigation absolute z-20 flex items-center justify-center
              w-[2.08vw] h-[2.08vw]
              top-[3.23vw] right-[9.53vw]
              rounded-full border text-white transition-transform duration-200
              ${
                isBeginning
                  ? 'cursor-not-allowed !border-[#8d8d92] hover:scale-100'
                  : 'cursor-pointer border-white hover:scale-110'
              }
            `}
            style={{
              backgroundColor: 'transparent',
            }}
            onClick={() => !isBeginning && swiperRef.current?.swiper?.slidePrev()}
          >
            <LeftArrowOutlined
              className={`text-[1.04vw] relative right-[1px] ${isBeginning ? '!text-[#8d8d92]' : 'text-white'}`}
            />
          </div>
        </>
      )}

      {/* 驾驶镜主题导航按钮容器 */}
      {!isWAP && theme === 'driving' && products.length > 4 && (
        <div className={`${theme === 'driving' ? 'pb-[20px] xl:pb-[80px] 2xl:pb-[120px]' : ''}`}>
          {/* 向左按钮 - 只在非开始位置显示 */}
          {!isBeginning && (
            <button
              className="absolute top-0 left-0 w-[120px] h-full flex items-center justify-end pb-[120px] box-border bg-gradient-to-r from-black to-transparent z-10 transition-opacity duration-300 cursor-pointer"
              onClick={() => swiperRef.current?.swiper?.slidePrev()}
            >
              <LeftArrowOutlined className="text-[46px] text-white mr-[13px]" />
            </button>
          )}

          {/* 向右按钮 - 只在非结束位置显示 */}
          {!isEnd && (
            <button
              className="absolute top-0 right-0 w-[120px] h-full flex items-center justify-start pb-[120px] box-border bg-gradient-to-l from-black to-transparent z-10 transition-opacity duration-300 cursor-pointer"
              onClick={() => swiperRef.current?.swiper?.slideNext()}
            >
              <RightArrowOutlined className="text-[46px] text-white ml-[13px]" />
            </button>
          )}
        </div>
      )}
    </div>
  );
}
